<script setup>
import { computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import EventSwitcher from './EventSwitcher.vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useStateStore } from '../stores/state.js'
import { fetchEventInfo, fetchSplits } from '../utils/checkpoint-data.js'
import { checkpointSummaries } from '../utils/checkpoint-results.js'
import { formatClock } from '../utils/polish-time.js'

// Wybór punktu kontrolnego: lista punktów z międzyczasami, liczba przejść i ostatnie wejście.
const REFRESH_MS = 30000

const route = useRoute()
const eventAlias = computed(() => route.params.eventAlias)

const data = ref(null)
const eventName = ref('')
const error = ref('')
let timer = null

const checkpoints = computed(() => data.value ? checkpointSummaries(data.value.markers, data.value.splits) : [])

async function load () {
  try {
    data.value = await fetchSplits(eventAlias.value)
    error.value = ''
  } catch (e) {
    error.value = 'Nie udało się pobrać punktów kontrolnych.'
  }
}

fetchEventInfo(eventAlias.value).then(info => { eventName.value = info.name }).catch(() => {})
load()
timer = setInterval(load, REFRESH_MS)

// EventSwitcher (przełączanie tras tych samych zawodów) czyta te magazyny.
useResultsStore().fetchEventDetails(eventAlias.value)
useStateStore().fetchEvents()

onUnmounted(() => clearInterval(timer))
</script>

<template>
  <div class="checkpoint-list">
    <EventSwitcher />
    <h1>Punkty kontrolne</h1>
    <p v-if="eventName" class="text-muted mb-1">{{ eventName }}</p>
    <p class="mb-3 small">
      <router-link :to="{ name: 'ArrivalChart', params: { eventAlias, markerId: 'finish' } }">Wykres przepływu na mecie</router-link>
      · na punkcie: otwórz punkt i wybierz „Wykres przepływu”
    </p>
    <div v-if="error" class="alert alert-warning">{{ error }}</div>
    <div v-else-if="!data" class="text-muted">Wczytywanie…</div>
    <div v-else-if="checkpoints.length === 0" class="text-muted">To wydarzenie nie ma punktów z międzyczasami.</div>
    <div v-else class="list-group">
      <router-link
        v-for="cp in checkpoints"
        :key="cp.id"
        :to="{ name: 'Checkpoint', params: { eventAlias, markerId: cp.id } }"
        class="list-group-item list-group-item-action checkpoint-row"
      >
        <span class="checkpoint-name">
          {{ cp.label }}
          <span v-if="cp.isStart && cp.label !== 'Start'" class="badge bg-secondary ms-1">Start</span>
          <span v-if="cp.isFinish && cp.label !== 'Meta'" class="badge bg-dark ms-1">Meta</span>
        </span>
        <span class="checkpoint-meta">
          <span class="badge rounded-pill pass-count">{{ cp.passCount }}</span>
          <span class="text-muted small">{{ cp.lastPassMs ? `ostatnie ${formatClock(cp.lastPassMs)}` : 'brak przejść' }}</span>
        </span>
      </router-link>
    </div>
  </div>
</template>

<style scoped>
.checkpoint-list {
  width: 100%;
  text-align: left;
}

@media (min-width: 768px) {
  .checkpoint-list {
    max-width: 640px;
    margin: 0 auto;
  }
}

.checkpoint-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.checkpoint-name {
  font-weight: 600;
  min-width: 0;
}

.checkpoint-meta {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
}

.pass-count {
  background: var(--sledz-primary);
  color: var(--sledz-panel-text);
  min-width: 2.2rem;
}
</style>
